/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSleep';

const ActionSleepManager: ActionManagerFn<'ActionSleep'> = (
  platform: Platform,
): ActionManager<'ActionSleep'> => {
  return {
    ...baseActionManager(platform, 'ActionSleep'),
    defaultValue,
    props: {
      ...baseActionProps,
      sleepTimeInMs: {
        label: 'Sleep Time In Milliseconds',
        type: 'numberInput',
        required: true,
      },
    },
    name: 'Sleep',
    getDisplayText: (item) => `${item.props.sleepTimeInMs}`,
    icon: { iconName: 'alarm-snooze' },
    description: `**Overview**<br/>
The Sleep action is a flow control component designed to introduce a deliberate pause in the execution of an action chain. It functions similarly to a setTimeout or Thread.sleep in traditional programming, halting the progression of the workflow for a specified duration before allowing the next action in the sequence to trigger.<br/><br/>
While modern asynchronous programming often relies on events and callbacks, this component is useful for specific timing scenarios where a fixed delay is necessary. It is often used to throttle rapid-fire operations, create perceptible pauses for better user experience (UX), or manage timing between interaction steps that might otherwise happen too quickly for the user to notice.

**Key Features**<br/>
* **Execution Delays:** Freezes the action chain for a precise amount of time, preventing the subsequent actions from running until the timer expires.
* **Millisecond Precision:** Accepts input in milliseconds (e.g., 1000ms = 1 second), allowing for both micro-delays (for UI rendering catch-up) and longer waits.
* **Simple Configuration:** Requires only a single numeric parameter to define the duration.

**Properties**<br/>
* **Sleep Time In Milliseconds (sleepTimeInMs) (Required):** A numeric property.
  * *Function:* Defines the duration of the pause.
  * *Example:* Entering 2000 will pause execution for 2 seconds.

**Use Cases**<br/>
* **UX Pacing:** Intentionally delaying the removal of a "Success" message or a loading spinner to ensure the user actually sees it before the screen navigates away.
* **Polling Loops:** Inside a Loop action, using Sleep to wait for 5 seconds between checks to an API endpoint to see if a long-running server job has finished.
* **Animation Sequencing:** Adding a small delay between two UI updates (like opening a drawer and then focusing a field) to allow the animation to complete smoothly.
* **Throttling:** Slowing down a loop that processes records to prevent overwhelming a rate-limited API or the browser's main thread.`,
  };
};

export default ActionSleepManager;
